import { ROLE_LABELS } from '@fos/shared/labels';
import { ArrowLeftRight, LogOut, UserRound } from 'lucide-react';
import { Link, Outlet, useNavigate } from 'react-router';
import { useSignOut } from '../../app/layout/UserMenus';
import { errorMessage } from '../../lib/api';
import { useActiveSession, useSwitchWorkspace } from '../../lib/auth';
import { trpc } from '../../lib/trpc';
import { Avatar } from '../../ui/Avatar';
import { Button } from '../../ui/Button';
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from '../../ui/Menu';
import { toast } from '../../ui/Toast';

/**
 * Układ portalu klienta (DECISIONS 2026-10-17 [P] „Model portalu klienta” nr 1): własny, prosty nagłówek bez menu
 * zespołu, ⌘K, Copilota, bąbla AI, dolnego menu i instalacji aplikacji. Nagłówek: nazwa workspace'u (firmy, która
 * prowadzi zgłoszenia) i klienta, po prawej osoba i „Wyloguj”. Dane z `portal.me` (sesja daje nazwy zanim przyjdą).
 * Osoba z kilkoma workspace'ami (np. konto klienta u dwóch firm) przełącza je w menu osoby.
 */
export function PortalLayout() {
  const session = useActiveSession();
  const me = trpc.portal.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const workspaces = trpc.identity.listMyWorkspaces.useQuery(undefined, { staleTime: 5 * 60_000 });
  const switchWorkspace = useSwitchWorkspace();
  const signOut = useSignOut();
  const navigate = useNavigate();

  const workspaceName = me.data?.workspace.name ?? session.workspace?.name ?? '';
  const clientName = me.data?.client.name ?? null;
  const personName = me.data?.person.name ?? session.user.name;
  const initials = me.data?.person.initials ?? session.user.initials;
  const others = (workspaces.data ?? []).filter((w) => w.id !== session.workspace?.id);

  return (
    <div className="flex min-h-dvh flex-col bg-app" data-portal="">
      <header className="border-b border-line bg-canvas">
        <div className="mx-auto flex min-h-16 w-full max-w-[784px] items-center gap-3 px-4 py-2.5 md:px-8">
          <Link
            to="/portal"
            className="flex min-w-0 flex-1 flex-col rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <span className="truncate text-body-m font-semibold text-fg">{workspaceName}</span>
            <span className="truncate text-caption text-fg-muted">
              Portal zgłoszeń{clientName ? ` · ${clientName}` : ''}
            </span>
          </Link>
          <Menu>
            <MenuTrigger asChild>
              <button
                type="button"
                aria-label={`Konto: ${personName}`}
                className="inline-flex h-11 shrink-0 items-center gap-2 rounded-full px-1.5 text-body-s text-fg-secondary hover:bg-soft-hover focus-visible:outline-2 focus-visible:outline-accent md:pr-3"
              >
                <Avatar initials={initials} color={session.user.avatarColor} size={32} />
                <span className="hidden max-w-[180px] truncate md:inline">{personName}</span>
              </button>
            </MenuTrigger>
            <MenuContent align="end" className="w-64">
              <MenuLabel>
                <span className="block truncate text-fg">{personName}</span>
                <span className="block truncate font-normal">{session.user.email}</span>
              </MenuLabel>
              <MenuItem icon={UserRound} onSelect={() => navigate('/portal/konto')}>
                Moje konto
              </MenuItem>
              {others.length > 0 && (
                <>
                  <MenuSeparator />
                  <MenuLabel>Przełącz na</MenuLabel>
                  {others.map((w) => (
                    <MenuItem
                      key={w.id}
                      icon={ArrowLeftRight}
                      onSelect={() =>
                        switchWorkspace.mutateAsync(w.id).then(
                          () => navigate(w.role === 'client' ? '/portal' : '/dzis'),
                          (err: unknown) => toast.error(errorMessage(err)),
                        )
                      }
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate">{w.name}</span>
                        <span className="text-caption text-fg-muted">{ROLE_LABELS[w.role]}</span>
                      </span>
                    </MenuItem>
                  ))}
                </>
              )}
              <MenuSeparator />
              <MenuItem icon={LogOut} onSelect={signOut}>
                Wyloguj
              </MenuItem>
            </MenuContent>
          </Menu>
          <Button variant="ghost" size="s" icon={LogOut} onClick={signOut} className="hidden md:inline-flex">
            Wyloguj
          </Button>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}
